import { useEffect, useMemo, useRef, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  ArrowLeft,
  ArrowRight,
  Banknote,
  Building2,
  CalendarDays,
  Check,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Coins,
  CreditCard,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  Lock,
  Paperclip,
  ShieldCheck,
  Gift,
  ClipboardList,
  House as HomeIcon,
  Info,
  PawPrint,
  Save,
  Search,
  Wallet,
} from "lucide-react";
import { Button, StatusBadge } from "@fawkes/ui";

import { formatDate as formatDatum, formatDateTime, formatEuro } from "../i18n/format";
import { fetchPinBijlageBlob, uploadPinBijlage } from "../api/dagafsluitingBijlage";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/**
 * Financieel → Dagafsluiting (maintainer design 2026-08-17): closing the drawer in four steps —
 * 1 check the day, 2 count the cash, 3 explain the difference, 4 four-eyes check and finish.
 *
 * <p>Steps 1 and 2 are built. Step 1 reads the day from {@code GET /api/dagafsluiting/stap1}: the cash
 * figures (opening balance, today in, today out, expected balance), the facts of the closing, the shelter's
 * own numbers of the day and the check-list — every point derived, so it cannot claim "ok" while it is not.
 * Step 2 counts the drawer per denomination; the counted total is arithmetic in the browser and the operator
 * can override it, because a drawer is what it is.
 *
 * <p>"Vandaag in cijfers" shows the day's own numbers. The api can also write a one-line positive summary
 * over exactly those numbers ({@code samenvatting=true}); the screen does not ask for it (maintainer:
 * "haal dit blokje maar weg"), so no AI is called here at all.
 */

interface Stap1 {
  kas: { beginkas: number; contanteInkomsten: number; contanteUitgaven: number; verwachtKassaldo: number };
  basis: {
    datum: string;
    vestigingId: number | null;
    vestigingNaam: string | null;
    kasNaam: string;
    openBoekingen: number;
    laatsteBoeking: string | null;
    afTeSluitenDoor: string | null;
  };
  cijfers: {
    dierenGeplaatst: number;
    dierenGeplaatstGisteren: number;
    verschilPercentage: number | null;
    adoptiesAfgerond: number;
    dierenHerenigd: number;
    donatiesOntvangen: number;
    donatiesBedrag: number;
    dierenIngeschreven: number;
  };
  samenvatting: string | null;
  controle: {
    datumKlopt: boolean;
    vestigingGekozen: boolean;
    geenOpenBoekingen: boolean;
    klaarOmTeTellen: boolean;
  };
  /** De afsluiting die deze dag al draagt, of null zolang de dag open staat. */
  afsluiting: Afsluiting | null;
  boekingen: {
    contantOntvangen: number;
    pinOntvangen: number;
    pinTransacties: number;
    totaalOntvangen: number;
    uitgaven: number;
    beginSaldo: number;
    verwachtEindsaldo: number;
  };
}

/** Een vastgelegde dagafsluiting, zoals de api hem teruggeeft. */
interface Afsluiting {
  dag: string;
  status: string;
  akkoord: boolean;
  contantGeteld: number;
  contantVerwacht: number;
  verschilContant: number;
  pinGeteld: number;
  pinVerwacht: number;
  verschilPin: number;
  /** De telstaat als "eenheid=aantal"-paren; hiermee zet het scherm een concept terug. */
  telstaat: string | null;
  /** De bijgevoegde pinafsluiting van deze dag, of null als er geen bij zit. */
  pinBijlageNaam: string | null;
  pinBijlageType: string | null;
  pinBijlageGrootte: number | null;
  opmerking: string | null;
  afgeslotenDoor: string | null;
  afgeslotenOp: string | null;
  akkoordNaam: string | null;
  akkoordOp: string | null;
}

/** The notes, biggest first — the order you count in. Euro notes start at € 5. */
const BILJETTEN = [200, 100, 50, 20, 10, 5] as const;

/** The coins: € 2 and € 1 are coins too, so they belong here and not with the notes. */
const MUNTEN = [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01] as const;

/** Everything you count, in one list — notes first, then coins. */
const EENHEDEN = [...BILJETTEN, ...MUNTEN] as const;

const STAPPEN = ["gegevens", "tellen", "verschil", "afronden"] as const;

export function Dagafsluiting() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [stap, setStap] = useState(0);
  // De dag die wordt afgesloten. Leeg = vandaag; de kalender zet er een andere dag in.
  const [dag, setDag] = useState<string>(() => vandaagIso());
  const [kalenderOpen, setKalenderOpen] = useState(false);
  const [kalenderMaand, setKalenderMaand] = useState<string>(() => vandaagIso().slice(0, 7));
  const [aantallen, setAantallen] = useState<Record<string, string>>({});
  const [pinBedrag, setPinBedrag] = useState("");
  const [pinAantal, setPinAantal] = useState("");
  const [handmatig, setHandmatig] = useState<string | null>(null);
  // De pinafsluiting (bonrol of foto ervan). Blijft bij de afsluiting horen; hij wordt met het afsluiten
  // meegestuurd zodra dat wordt vastgelegd.
  const [pinBijlage, setPinBijlage] = useState<
    { ref: string | null; naam: string; grootte: number; type: string; url: string | null } | null
  >(null);
  const [bijlageBezig, setBijlageBezig] = useState(false);
  const [bijlageFout, setBijlageFout] = useState<string | null>(null);
  const [collega, setCollega] = useState("");
  const [wachtwoord, setWachtwoord] = useState("");
  const [wachtwoordZichtbaar, setWachtwoordZichtbaar] = useState(false);
  const [akkoord, setAkkoord] = useState<{ loginName: string; naam: string } | null>(null);
  const [akkoordFout, setAkkoordFout] = useState<string | null>(null);
  // Vier ogen mag ook later: de dag wordt dan afgesloten met "wacht op controle", en een collega geeft
  // het akkoord op een rustiger moment. Zonder dit zou een avonddienst alleen nooit kunnen afsluiten.
  const [laterControleren, setLaterControleren] = useState(false);
  // De opmerking bij de afsluiting: waarom er een verschil is, of wat er verder aan de hand was.
  // Hij hoort bij de vastgelegde afsluiting, niet bij het scherm — een verschil zonder uitleg is later
  // niet meer te reconstrueren.
  const [opmerking, setOpmerking] = useState("");
  // De vastgelegde afsluiting, zodra hij is opgeslagen.
  const [afgesloten, setAfgesloten] = useState<Afsluiting | null>(null);
  const [afsluitFout, setAfsluitFout] = useState<string | null>(null);
  // Het tijdstip waarop het concept voor het laatst is geparkeerd — de enige bevestiging die deze knop
  // nodig heeft; een dialoog zou het tellen alleen maar onderbreken.
  const [conceptOp, setConceptOp] = useState<string | null>(null);
  // De dag waarvan de bestaande opmerking al is voorgevuld, zodat dat precies één keer gebeurt.
  const voorgevuldeDag = useRef<string | null>(null);
  // Staat de lijst met boekingen van deze dag open? Die is waar je een verschil gaat uitzoeken.
  const [uitzoeken, setUitzoeken] = useState(false);
  // De facturen van deze dag: het andere spoor waarlangs een verschil verklaard wordt.
  const [facturenOpen, setFacturenOpen] = useState(false);

  // De vestiging waar de shell op staat: zonder die id kan de api de vestiging niet noemen en blijft het
  // controlepunt "juiste vestiging" open staan.
  const { data: vestigingFeed } = useCustom<{ id: number; naam: string }>({
    url: "vestigingen/current", method: "get",
  });
  const vestigingId = vestigingFeed?.data?.id ?? null;

  const { data, isLoading, isError, refetch } = useCustom<Stap1>({
    url: `dagafsluiting/stap1?dag=${dag}&samenvatting=false${vestigingId ? `&vestigingId=${vestigingId}` : ""}`,
    method: "get",
    queryOptions: { enabled: vestigingId != null },
  });

  // De dagen van de getoonde maand die al een afsluiting hebben — die kleuren groen in de kalender.
  const { data: dagenFeed } = useCustom<{ datum: string; afgesloten: boolean; akkoord: boolean }[]>({
    url: `dagafsluiting/dagen?maand=${kalenderMaand}-01${vestigingId ? `&vestigingId=${vestigingId}` : ""}`,
    method: "get",
    queryOptions: { enabled: vestigingId != null && kalenderOpen },
  });
  const afgeslotenDagen = useMemo(() => {
    const rijen = dagenFeed?.data;
    const kaart = new Map<string, boolean>();
    if (Array.isArray(rijen)) {
      rijen.forEach((d) => kaart.set(d.datum, d.akkoord));
    }
    return kaart;
  }, [dagenFeed]);
  // De collega's die akkoord kunnen geven: de api laat degene die afsluit er zelf uit — vier ogen is
  // twee mensen, dus je eigen naam aanbieden zou alleen tot een geweigerd akkoord leiden.
  // De telling van de laatst afgesloten dag, voor de knop "Overnemen van vorige dag". Je telt de kas
  // meestal in dezelfde coupures als de vorige keer; dat overtikken hoeft niet.
  const { data: vorigeTellingFeed } = useCustom<{ dag: string | null; telstaat: string | null; pinGeteld: number | null }>({
    url: `dagafsluiting/vorige-telling?dag=${dag}${vestigingId ? `&vestigingId=${vestigingId}` : ""}`,
    method: "get",
    queryOptions: { enabled: vestigingId != null },
  });
  const vorigeTelling = vorigeTellingFeed?.data ?? null;
  const kanOvernemen = (vorigeTelling?.telstaat ?? "").length > 0;
  const neemVorigeOver = () => {
    if (!vorigeTelling?.telstaat) return;
    setAantallen(uitTelstaat(vorigeTelling.telstaat));
    if (vorigeTelling.pinGeteld != null) {
      setPinBedrag(String(vorigeTelling.pinGeteld));
    }
  };

  const { data: collegaFeed } = useCustom<{ loginName: string; naam: string }[]>({
    url: "dagafsluiting/collegas", method: "get",
  });
  const collegas = useMemo(() => {
    const rijen = collegaFeed?.data;
    return Array.isArray(rijen) ? rijen : [];
  }, [collegaFeed]);

  // De boekingen van de gekozen dag: hiermee zoek je een verschil uit zonder het scherm te verlaten.
  const { data: boekingenVanDag, isLoading: boekingenBezig } = useCustom<{
    items: {
      id: number; soort: string; geboektOp: string; omschrijving: string; bedrag: number;
      grootboekRekening: string | null; verwerkt: boolean;
    }[];
    total: number;
  }>({
    url: `kasboek?van=${dag}&tot=${dag}&size=100${vestigingId ? `&vestigingId=${vestigingId}` : ""}`,
    method: "get",
    queryOptions: { enabled: uitzoeken },
  });

  // De facturen die op deze dag zijn gemaakt, met wie ze betreft en wat er open staat.
  const { data: facturenVanDag, isLoading: facturenBezig } = useCustom<{
    items: {
      id: number; nummer: string | null; klantNaam: string | null; dierNaam: string | null;
      totaal: number | null; betaald: number | null; openstaand: number | null; concept?: boolean;
    }[];
    total: number;
  }>({
    url: `facturen/overzicht?from=${dag}&to=${dag}&size=100&sort=nieuwste`,
    method: "get",
    queryOptions: { enabled: facturenOpen },
  });

  const { mutate: vraagAkkoord, isLoading: akkoordBezig } = useCustomMutation();
  const { mutate: legVast, isLoading: afsluitBezig } = useCustomMutation();

  const stap1 = data?.data ?? null;
  // Een dag die al is afgesloten toont zijn eigen afsluiting; de opmerking die erbij hoort komt mee,
  // zodat opnieuw afsluiten die uitleg niet stilletjes wist.
  const bestaande = stap1?.afsluiting ?? null;
  // Een andere dag begint schoon.
  useEffect(() => {
    setAfgesloten(null);
    setAfsluitFout(null);
    setOpmerking("");
    setPinBijlage(null);
    setBijlageFout(null);
    voorgevuldeDag.current = null;
  }, [dag]);
  // De opmerking van een al afgesloten dag komt één keer mee — daarna is wat de operator typt leidend, en
  // het opnieuw ophalen van de dag (na afsluiten) mag die tekst niet terugdraaien.
  useEffect(() => {
    if (bestaande && voorgevuldeDag.current !== bestaande.dag) {
      voorgevuldeDag.current = bestaande.dag;
      setOpmerking(bestaande.opmerking ?? "");
      // Een geparkeerd concept komt terug zoals het is achtergelaten: de getelde aantallen en het
      // pinbedrag. Bij een al afgesloten dag gebeurt dat niet — die telling is vastgesteld, en hem
      // ongevraagd in de invoervelden zetten nodigt uit tot een tweede afsluiting die niemand vroeg.
      if (bestaande.status === "CONCEPT") {
        setAantallen(uitTelstaat(bestaande.telstaat));
        setPinBedrag(bestaande.pinGeteld ? String(bestaande.pinGeteld) : "");
        setConceptOp(bestaande.afgeslotenOp);
      }
      // De eerder bijgevoegde bonrol hoort er weer bij te staan — zonder url, want de bytes komen pas
      // als iemand hem opent (achter de bearer langs).
      setPinBijlage(bestaande.pinBijlageNaam
        ? {
            ref: null,
            naam: bestaande.pinBijlageNaam,
            grootte: bestaande.pinBijlageGrootte ?? 0,
            type: bestaande.pinBijlageType ?? "",
            url: null,
          }
        : null);
    }
  }, [bestaande]);

  /** Het concept parkeren: dezelfde payload als afsluiten, maar de dag blijft open. */
  const parkeer = () => {
    if (vestigingId == null) {
      return;
    }
    setAfsluitFout(null);
    legVast(
      { url: "dagafsluiting/afsluiten", method: "post", values: { ...afsluitPayload(), concept: true } },
      {
        onSuccess: (res) => {
          const data = (res?.data ?? null) as Afsluiting | null;
          setConceptOp(data?.afgeslotenOp ?? new Date().toISOString());
          refetch();
        },
        onError: () => setAfsluitFout(t("dagafsluiting.conceptFout")),
      },
    );
  };
  /**
   * Wat er van deze dag al vastligt, en dus wat elke stap van de balk laat zien.
   *
   * <p><b>De balk leest de dag, niet de wizard.</b> Hij toonde alleen hoever je zelf geklikt had, dus
   * een dag die gisteren is geteld en afgesloten zag eruit alsof er nog niets gebeurd was -- je moest
   * tot stap 4 doorklikken om te zien dat hij al akkoord was (Maarten, 2026-10-23).
   */
  const vastgelegd = afgesloten ?? bestaande;
  const dagAfgesloten = !!vastgelegd && vastgelegd.status !== "CONCEPT";
  const dagAkkoord = !!vastgelegd?.akkoord;
  const isGeteld = dagAfgesloten || !!vastgelegd?.telstaat;
  // Een verschil zonder uitleg is niet "klaar": daar hoort iemand nog naar te kijken.
  const vastVerschil = !!vastgelegd && (vastgelegd.verschilContant !== 0 || vastgelegd.verschilPin !== 0);
  const verschilToegelicht = !!vastgelegd?.opmerking?.trim();
  const stapStand = (i: number): "open" | "ok" | "letop" | "wacht" => {
    if (i === 0) return stap1?.controle.klaarOmTeTellen || isGeteld ? "ok" : "open";
    if (i === 1) return isGeteld ? "ok" : "open";
    if (i === 2) {
      if (!isGeteld) return "open";
      return vastVerschil && !verschilToegelicht ? "letop" : "ok";
    }
    if (!dagAfgesloten) return "open";
    return dagAkkoord ? "ok" : "wacht";
  };

  // Een api zonder het boekingen-blok (oudere build) mag het scherm niet leegmaken.
  const boekingen = stap1?.boekingen ?? {
    contantOntvangen: 0, pinOntvangen: 0, pinTransacties: 0, totaalOntvangen: 0,
    uitgaven: 0, beginSaldo: 0, verwachtEindsaldo: 0,
  };
  const kas = stap1?.kas ?? { beginkas: 0, contanteInkomsten: 0, contanteUitgaven: 0, verwachtKassaldo: 0 };

  // Counting is arithmetic: every denomination times its count. Rounded to cents once, at the end —
  // 0.1 + 0.2 in floating point is not 0.3, and a cash count may not drift.
  const getaldTotaal = useMemo(
    () => Math.round(EENHEDEN.reduce((som, eenheid) => {
      const aantal = Number((aantallen[String(eenheid)] ?? "").trim() || 0);
      return som + (Number.isFinite(aantal) ? aantal * eenheid : 0);
    }, 0) * 100) / 100,
    [aantallen],
  );
  /** The coins as one amount — what step 3 shows on its "Munten" line. */
  const muntenTotaal = useMemo(
    () => Math.round(MUNTEN.reduce((som, eenheid) => {
      const aantal = Number((aantallen[String(eenheid)] ?? "").trim() || 0);
      return som + (Number.isFinite(aantal) ? aantal * eenheid : 0);
    }, 0) * 100) / 100,
    [aantallen],
  );
  // De pinautomaat begint op wat er vandaag aan pin geboekt is: dat is waar de bonrol tegenaan wordt
  // gehouden. Zodra de gebruiker iets intypt, wint dat — een lege telling is geen telling van nul.
  const getaldPin = pinBedrag.trim() === "" ? boekingen.pinOntvangen : bedragVan(pinBedrag);
  const getaldPinAantal = pinAantal.trim() === "" ? boekingen.pinTransacties : Number(pinAantal.trim()) || 0;
  /** Wat er van deze dag wordt vastgelegd — één opzet, of hij nu geparkeerd of afgesloten wordt. */
  const afsluitPayload = () => ({
    dag,
    vestigingId,
    telling: {
      contantGeteld: getaldTotaal,
      pinGeteld: Number((pinBedrag || "").replace(",", ".")) || 0,
      pinTransacties: Number(pinAantal || 0) || 0,
      biljetten: EENHEDEN.map((eenheid) => ({
        eenheid,
        aantal: Number((aantallen[String(eenheid)] ?? "").trim() || 0) || 0,
      })).filter((r) => r.aantal > 0),
      munten: muntenTotaal,
    },
    akkoordDoor: akkoord?.loginName ?? null,
    akkoordNaam: akkoord?.naam ?? null,
    opmerking: opmerking.trim() || null,
    // Alleen een net geüploade bijlage gaat mee; is er niets nieuws, dan blijft staan wat er staat.
    pinBijlage: pinBijlage?.ref
      ? {
          ref: pinBijlage.ref,
          naam: pinBijlage.naam,
          type: pinBijlage.type,
          grootte: pinBijlage.grootte,
        }
      : null,
  });

  const erIsGeteld = getaldTotaal > 0 || pinBedrag.trim() !== "";
  // Een object-URL blijft in het geheugen hangen tot je hem intrekt.
  useEffect(() => () => { if (pinBijlage?.url) URL.revokeObjectURL(pinBijlage.url); }, [pinBijlage]);

  const verschilContant = Math.round((getaldTotaal - boekingen.verwachtEindsaldo) * 100) / 100;
  const verschilPin = Math.round((getaldPin - boekingen.pinOntvangen) * 100) / 100;
  const heeftVerschil = erIsGeteld && (verschilContant !== 0 || verschilPin !== 0);
  const geteld = handmatig != null && handmatig.trim() !== ""
    ? Number(handmatig.replace(",", ".")) || 0
    : Math.round(getaldTotaal * 100) / 100;

  return (
    <div className="fawkes-fin fawkes-dag">
      <header className="fawkes-fin__head">
        <span className="fawkes-fin__head-icon" aria-hidden="true"><Wallet size={26} /></span>
        <div className="fawkes-fin__head-text">
          <h2>{t("dagafsluiting.title")}</h2>
          <p>{t("dagafsluiting.subtitle")}</p>
        </div>
        <div className="fawkes-fin__kpis">
          <Kpi icon={<Wallet size={18} />} label={t("dagafsluiting.kpi.beginkas")}
            value={formatEuro(kas.beginkas, locale)} />
          <Kpi icon={<Coins size={18} />} label={t("dagafsluiting.kpi.inkomsten")}
            value={formatEuro(kas.contanteInkomsten, locale)} />
          <Kpi icon={<Coins size={18} />} label={t("dagafsluiting.kpi.uitgaven")}
            value={formatEuro(kas.contanteUitgaven, locale)} />
          <Kpi icon={<Banknote size={18} />} label={t("dagafsluiting.kpi.verwacht")}
            value={formatEuro(kas.verwachtKassaldo, locale)} accent />
        </div>
      </header>

      <ol className="fawkes-dag__stepper">
        {STAPPEN.map((naam, i) => {
          const stand = stapStand(i);
          // Een afgesloten dag mag je helemaal teruglezen; een open dag alleen tot waar je bent.
          const teOpenen = i <= stap || dagAfgesloten;
          return (
            <li
              key={naam}
              className={`fawkes-dag__step${i === stap ? " is-active" : ""}`
                + (stand === "ok" ? " is-done" : stand === "letop" ? " is-letop" : stand === "wacht" ? " is-wacht" : "")}
            >
              <button type="button" onClick={() => teOpenen && setStap(i)} disabled={!teOpenen}>
                <span className="fawkes-dag__step-nr" aria-hidden="true">
                  {stand === "ok" ? <Check size={14} />
                    : stand === "letop" ? <AlertTriangle size={14} />
                      : stand === "wacht" ? <Clock size={14} />
                        : i + 1}
                </span>
                <span className="fawkes-dag__step-tekst">
                  {t(`dagafsluiting.stap.${naam}`)}
                  <small>{t(`dagafsluiting.stapStand.${stand}`)}</small>
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      {/* Of de dag dicht is, en door wie -- zonder dat je naar stap 4 hoeft te klikken. */}
      {dagAfgesloten && vastgelegd && (
        <p className={`fawkes-dag__dagstand${dagAkkoord ? " is-ok" : " is-wacht"}`}>
          {dagAkkoord ? <Lock size={16} aria-hidden="true" /> : <Clock size={16} aria-hidden="true" />}
          <span>
            <strong>
              {t("dagafsluiting.dagstand.afgesloten", {
                args: [
                  vastgelegd.afgeslotenDoor ?? "—",
                  vastgelegd.afgeslotenOp ? formatDateTime(vastgelegd.afgeslotenOp, locale) : "—",
                ],
              })}
            </strong>
            {dagAkkoord
              ? ` ${t("dagafsluiting.dagstand.akkoord", {
                args: [
                  vastgelegd.akkoordNaam ?? "—",
                  vastgelegd.akkoordOp ? formatDateTime(vastgelegd.akkoordOp, locale) : "—",
                ],
              })}`
              : ` ${t("dagafsluiting.dagstand.wachtAkkoord")}`}
          </span>
        </p>
      )}

      {isLoading && <p className="fawkes-fin__state">{t("common.loading")}</p>}
      {isError && (
        <p className="fawkes-fin__state fawkes-fin__state--error">
          {t("common.loadError")}{" "}
          <Button variant="secondary" onClick={() => refetch()}>{t("common.retry")}</Button>
        </p>
      )}

      {!isLoading && !isError && stap1 && stap === 0 && (
        <section className="fawkes-dag__card">
          <header>
            <h3>{t("dagafsluiting.stap1.titel")}</h3>
            <p>{t("dagafsluiting.stap1.uitleg")}</p>
          </header>

          <div className="fawkes-dag__kolommen">
            <div className="fawkes-dag__blok">
              <h4>{t("dagafsluiting.basis")}</h4>
              <dl className="fawkes-dag__lijst">
                <div className="fawkes-dag__regel fawkes-dag__datumregel">
                  <dt>
                    <span className={`fawkes-dag__vink${stap1.controle.datumKlopt ? " is-ok" : ""}`}
                      aria-hidden="true">
                      {stap1.controle.datumKlopt ? <CheckCircle2 size={16} /> : <CalendarDays size={16} />}
                    </span>
                    {t("dagafsluiting.basis.datum")}
                  </dt>
                  <dd>
                    <button
                      type="button" className="fawkes-dag__datumknop"
                      aria-haspopup="dialog" aria-expanded={kalenderOpen}
                      onClick={() => { setKalenderMaand(dag.slice(0, 7)); setKalenderOpen((v) => !v); }}
                    >
                      {formatDatum(dag, locale)} <CalendarDays size={14} aria-hidden="true" />
                    </button>
                    {kalenderOpen && (
                      <Kalender
                        maand={kalenderMaand}
                        gekozen={dag}
                        afgesloten={afgeslotenDagen}
                        locale={locale}
                        t={t}
                        onMaand={setKalenderMaand}
                        onKies={(d) => { setDag(d); setKalenderOpen(false); }}
                        onSluit={() => setKalenderOpen(false)}
                      />
                    )}
                  </dd>
                </div>
                <Regel icon={<Building2 size={14} />} label={t("financieel.filter.vestiging")}
                  waarde={stap1.basis.vestigingNaam} ok={stap1.controle.vestigingGekozen} />
                <Regel icon={<Wallet size={14} />} label={t("dagafsluiting.basis.kas")}
                  waarde={stap1.basis.kasNaam} ok />
                <Regel icon={<CalendarDays size={14} />} label={t("dagafsluiting.basis.laatste")}
                  waarde={stap1.basis.laatsteBoeking
                    ? formatDateTime(stap1.basis.laatsteBoeking, locale)
                    : t("dagafsluiting.basis.geenBoeking")}
                  ok={!!stap1.basis.laatsteBoeking} />
                <Regel icon={<CheckCircle2 size={14} />} label={t("dagafsluiting.basis.door")}
                  waarde={stap1.basis.afTeSluitenDoor} ok={!!stap1.basis.afTeSluitenDoor} />
              </dl>
            </div>

            <div className="fawkes-dag__blok">
              <h4>{t("dagafsluiting.cijfers")}</h4>
              <div className="fawkes-dag__cijfers">
                <Cijfer icon={<PawPrint size={20} />} waarde={String(stap1.cijfers.dierenGeplaatst)}
                  label={t("dagafsluiting.cijfer.geplaatst")} />
                <Cijfer icon={<ClipboardList size={20} />} waarde={String(stap1.cijfers.dierenIngeschreven)}
                  label={t("dagafsluiting.cijfer.ingeschreven")} />
                <Cijfer icon={<HomeIcon size={20} />} waarde={String(stap1.cijfers.dierenHerenigd)}
                  label={t("dagafsluiting.cijfer.herenigd")} />
                {/* Donaties in euro's: wat er binnenkwam zegt meer dan hoe vaak er iets binnenkwam. */}
                {/* Zonder centen, zoals in het ontwerp: een tegel toont de orde van grootte, de cent
                    staat in het kasboek. */}
                <Cijfer icon={<Gift size={20} />}
                  waarde={heleEuros(stap1.cijfers.donatiesBedrag ?? 0, locale)}
                  label={t("dagafsluiting.cijfer.donaties")} />
              </div>
            </div>

            <div className="fawkes-dag__blok">
              <h4>{t("dagafsluiting.controle")}</h4>
              <ul className="fawkes-dag__checks">
                <Punt ok={stap1.controle.datumKlopt} tekst={t("dagafsluiting.check.datum")} />
                <Punt ok={stap1.controle.vestigingGekozen} tekst={t("dagafsluiting.check.vestiging")} />
                <Punt ok={stap1.controle.geenOpenBoekingen} tekst={t("dagafsluiting.check.open")} />
                <Punt ok={stap1.controle.klaarOmTeTellen} tekst={t("dagafsluiting.check.klaar")} />
              </ul>
            </div>
          </div>

          <footer className="fawkes-dag__acties">
            <Conceptknop bezig={afsluitBezig} kan={vestigingId != null} op={conceptOp}
              onClick={parkeer} locale={locale} t={t} />
            <Button onClick={() => setStap(1)} disabled={!stap1.controle.klaarOmTeTellen}>
              {t("dagafsluiting.verder.tellen")} <ArrowRight size={14} />
            </Button>
          </footer>
        </section>
      )}

      {!isLoading && !isError && stap1 && stap === 1 && (
        <section className="fawkes-dag__card">
          <header>
            <h3>{t("dagafsluiting.stap2.titel")}</h3>
            <p>{t("dagafsluiting.stap2.uitleg")}</p>
          </header>

          <div className="fawkes-dag__tellen">
            <div className="fawkes-dag__blok">
              <h4>{t("dagafsluiting.biljetten")}</h4>
              {/* Biljetten links, munten rechts: veertien regels onder elkaar dwongen tot scrollen, en een
                  kas tel je toch al in twee stapels. */}
              <div className="fawkes-dag__telkolommen">
                <Telstaat
                  titel={t("dagafsluiting.kopBiljetten")} eenheden={BILJETTEN} soort="biljet"
                  aantallen={aantallen} setAantallen={setAantallen} locale={locale} t={t}
                />
                <Telstaat
                  titel={t("dagafsluiting.kopMunten")} eenheden={MUNTEN} soort="munt"
                  aantallen={aantallen} setAantallen={setAantallen} locale={locale} t={t}
                />
              </div>
              {/* Overnemen zet de vorige telling in de velden; je telt daarna gewoon na en past aan.
                  Zonder een eerdere afsluiting valt er niets over te nemen, dan staat hij uit. */}
              <div className="fawkes-dag__overnemen">
                <Button variant="secondary" disabled={!kanOvernemen} onClick={neemVorigeOver}
                  title={kanOvernemen ? undefined : t("dagafsluiting.overnemenGeenVorige")}>
                  {t("dagafsluiting.overnemen")}
                </Button>
                {kanOvernemen && vorigeTelling?.dag && (
                  <span className="fawkes-dag__overnemen-hint">
                    {t("dagafsluiting.overnemenVan", { args: [vorigeTelling.dag] })}
                  </span>
                )}
              </div>
              <div className="fawkes-dag__teltotaal">
                <span>{t("dagafsluiting.totaalGeteld")}</span>
                <strong className="tabular">{formatEuro(getaldTotaal, locale)}</strong>
              </div>
            </div>

            <div className="fawkes-dag__telzij">
              <div className="fawkes-dag__blok">
                <h4>{t("dagafsluiting.controle")}</h4>
                <ul className="fawkes-dag__checks">
                  <Punt ok={getaldTotaal > 0} tekst={t("dagafsluiting.check.geteld")} />
                  <Punt ok={getaldTotaal > 0} tekst={t("dagafsluiting.check.losGeld")} />
                  <Punt ok={stap1.controle.geenOpenBoekingen} tekst={t("dagafsluiting.check.open")} />
                </ul>
              </div>

              <div className="fawkes-dag__blok">
                <h4>{t("dagafsluiting.pin")}</h4>
                <div className="fawkes-dag__pin">
                  <label>
                    <span className="fui-field__label">{t("dagafsluiting.pinTransacties")}</span>
                    <input
                      className="fui-field__input" inputMode="numeric" value={pinAantal}
                      placeholder={String(boekingen.pinTransacties)}
                      onChange={(e) => setPinAantal(e.target.value)}
                    />
                  </label>
                  <label>
                    <span className="fui-field__label">{t("dagafsluiting.pinBedrag")}</span>
                    <input
                      className="fui-field__input" inputMode="decimal" value={pinBedrag}
                      placeholder={String(boekingen.pinOntvangen).replace(".", ",")}
                      onChange={(e) => setPinBedrag(e.target.value)}
                    />
                  </label>
                </div>
                <p className="fawkes-dag__hint">{t("dagafsluiting.pinHint")}</p>
                {bijlageFout && (
                  <p className="fawkes-inboeken__field-error" role="alert">{bijlageFout}</p>
                )}

                {/* De bonrol van de pinautomaat hoort bij de afsluiting: een foto of pdf is het bewijs
                    waar een controleur later naar kijkt. */}
                {pinBijlage ? (
                  <div className="fawkes-dag__bijlage">
                    {pinBijlage.type.startsWith("image/") && pinBijlage.url ? (
                      <img src={pinBijlage.url} alt={pinBijlage.naam} />
                    ) : (
                      <span className="fawkes-dag__bijlage-icoon" aria-hidden="true"><FileText size={20} /></span>
                    )}
                    <div>
                      {pinBijlage.url ? (
                        <a href={pinBijlage.url} target="_blank" rel="noreferrer">{pinBijlage.naam}</a>
                      ) : (
                        /* De opgeslagen bijlage: de bytes komen achter de bearer vandaan, dus een gewone
                           link zou een 401 openen. Deze knop haalt ze op en opent de blob. */
                        <button type="button" className="fawkes-fin__link" onClick={() => {
                          fetchPinBijlageBlob(dag, vestigingId)
                            .then((blob) => globalThis.open(URL.createObjectURL(blob), "_blank"))
                            .catch(() => setBijlageFout(t("dagafsluiting.bijlageFout")));
                        }}>
                          {pinBijlage.naam}
                        </button>
                      )}
                      <small>
                        {kilobytes(pinBijlage.grootte)}
                        {bijlageBezig ? ` · ${t("common.loading")}` : ""}
                      </small>
                    </div>
                    <button
                      type="button" className="fawkes-fin__link"
                      onClick={() => {
                        if (pinBijlage.url) URL.revokeObjectURL(pinBijlage.url);
                        setPinBijlage(null);
                      }}
                    >
                      {t("dagafsluiting.bijlageVerwijderen")}
                    </button>
                  </div>
                ) : (
                  <label className="fawkes-dag__bijlageknop">
                    <Paperclip size={14} aria-hidden="true" /> {t("dagafsluiting.bijlageToevoegen")}
                    <input
                      type="file" accept="image/*,application/pdf" capture="environment"
                      onChange={(e) => {
                        const bestand = e.target.files?.[0];
                        e.target.value = "";
                        if (!bestand) return;
                        // Meteen uploaden: de ref hoort bij de afsluiting, en een bestand dat alleen in
                        // het tabblad leeft is bij de eerstvolgende herlaadbeurt weg.
                        setBijlageFout(null);
                        setBijlageBezig(true);
                        const lokaal = URL.createObjectURL(bestand);
                        setPinBijlage({
                          ref: null, naam: bestand.name, grootte: bestand.size,
                          type: bestand.type, url: lokaal,
                        });
                        uploadPinBijlage(bestand)
                          .then((opgeslagen) => {
                            setPinBijlage((cur) => (cur ? { ...cur, ref: opgeslagen.ref } : cur));
                          })
                          .catch(() => {
                            URL.revokeObjectURL(lokaal);
                            setPinBijlage(null);
                            setBijlageFout(t("dagafsluiting.bijlageFout"));
                          })
                          .finally(() => setBijlageBezig(false));
                      }}
                    />
                  </label>
                )}
              </div>

              <div className="fawkes-dag__blok fawkes-dag__geteld">
                <h4><Wallet size={16} aria-hidden="true" /> {t("dagafsluiting.getaldSaldo")}</h4>
                <input
                  className="fui-field__input fawkes-dag__getaldveld"
                  inputMode="decimal"
                  value={handmatig ?? formatEuro(geteld, locale)}
                  aria-label={t("dagafsluiting.getaldSaldo")}
                  onFocus={() => setHandmatig((h) => h ?? String(geteld).replace(".", ","))}
                  onChange={(e) => setHandmatig(e.target.value)}
                />
                <p className="fawkes-dag__hint">{t("dagafsluiting.pasAan")}</p>
              </div>

              <p className="fawkes-dag__tip">
                <Info size={14} aria-hidden="true" /> {t("dagafsluiting.tip")}
              </p>
            </div>
          </div>

          <footer className="fawkes-dag__acties">
            <Button variant="secondary" onClick={() => setStap(0)}>
              <ArrowLeft size={14} /> {t("dagafsluiting.vorige")}
            </Button>
            <Conceptknop bezig={afsluitBezig} kan={vestigingId != null} op={conceptOp}
              onClick={parkeer} locale={locale} t={t} />
            <Button onClick={() => setStap(2)} disabled={geteld <= 0}>
              {t("dagafsluiting.verder.verschil")} <ArrowRight size={14} />
            </Button>
          </footer>
        </section>
      )}

      {!isLoading && !isError && stap1 && stap === 2 && (
        <section className="fawkes-dag__card">
          <header>
            <h3>{t("dagafsluiting.stap3.titel")}</h3>
            <p>{t("dagafsluiting.stap3.uitleg")}</p>
          </header>

          {/* Een verschil hoort niet in een hoek te staan: het is de reden dat deze stap bestaat. */}
          {heeftVerschil && (
            <div className="fawkes-dag__alarm" role="alert">
              <span aria-hidden="true"><AlertTriangle size={20} /></span>
              <div>
                <strong>{t("dagafsluiting.erIsVerschil")}</strong>
                <p>
                  {verschilContant !== 0 && t(
                    verschilContant > 0
                      ? "dagafsluiting.verschilContantMeer"
                      : "dagafsluiting.verschilContantMinder",
                    { args: [formatEuro(Math.abs(verschilContant), locale)] },
                  )}
                  {verschilContant !== 0 && verschilPin !== 0 ? " " : ""}
                  {verschilPin !== 0 && t(
                    verschilPin > 0 ? "dagafsluiting.verschilPinMeer" : "dagafsluiting.verschilPinMinder",
                    { args: [formatEuro(Math.abs(verschilPin), locale)] },
                  )}
                </p>
              </div>
              <div className="fawkes-dag__alarm-acties">
                <Button variant="secondary" onClick={() => setUitzoeken((v) => !v)}>
                  <Search size={14} />{" "}
                  {t(uitzoeken ? "dagafsluiting.verbergBoekingen" : "dagafsluiting.zoekUit")}
                </Button>
                <Button variant="secondary" onClick={() => setFacturenOpen((v) => !v)}>
                  <FileText size={14} />{" "}
                  {t(facturenOpen ? "dagafsluiting.verbergFacturen" : "dagafsluiting.bekijkFacturen")}
                </Button>
              </div>
            </div>
          )}

          {uitzoeken && (
            <div className="fawkes-dag__blok">
              <h4><Search size={16} aria-hidden="true" /> {t("dagafsluiting.boekingenVanDag")}</h4>
              {boekingenBezig && <p className="fawkes-fin__state">{t("common.loading")}</p>}
              {!boekingenBezig && (boekingenVanDag?.data?.items ?? []).length === 0 && (
                <p className="fawkes-dag__hint">{t("dagafsluiting.geenBoekingenDag")}</p>
              )}
              {!boekingenBezig && (boekingenVanDag?.data?.items ?? []).length > 0 && (
                <IndeelbareTabel
                  tabelKey="dagafsluiting-boekingen"
                  columns={[
                    { key: "datum", header: t("kasboek.kolom.datum"),
                      cell: (b) => <span className="tabular">{formatDateTime(b.geboektOp, locale)}</span> },
                    {
                      key: "type", header: t("kasboek.kolom.type"),
                      cell: (b) => (
                        <StatusBadge tone={b.soort === "INKOMST" ? "success" : "danger"}>
                          {t(`kasboek.soort.${b.soort}`)}
                        </StatusBadge>
                      ),
                    },
                    { key: "omschrijving", header: t("kasboek.kolom.omschrijving"), cell: (b) => b.omschrijving },
                    { key: "grootboek", header: t("omzet.kolom.grootboek"), cell: (b) => b.grootboekRekening ?? "—" },
                    {
                      key: "bedrag", header: t("kasboek.kolom.bedrag"), align: "right",
                      cell: (b) => (
                        <span className={b.soort === "UITGAVE" ? "tabular is-danger" : "tabular"}>
                          {b.soort === "UITGAVE" ? "-" : ""}{formatEuro(b.bedrag, locale)}
                        </span>
                      ),
                    },
                  ]}
                  rows={boekingenVanDag?.data?.items ?? []}
                  getRowKey={(b) => String(b.id)}
                  empty=""
                />
              )}
              <p className="fawkes-dag__hint">{t("dagafsluiting.uitzoekTip")}</p>
            </div>
          )}

          {facturenOpen && (
            <div className="fawkes-dag__blok">
              <h4><FileText size={16} aria-hidden="true" /> {t("dagafsluiting.facturenVanDag")}</h4>
              {facturenBezig && <p className="fawkes-fin__state">{t("common.loading")}</p>}
              {!facturenBezig && (facturenVanDag?.data?.items ?? []).length === 0 && (
                <p className="fawkes-dag__hint">{t("dagafsluiting.geenFacturenDag")}</p>
              )}
              {!facturenBezig && (facturenVanDag?.data?.items ?? []).length > 0 && (
                <IndeelbareTabel
                  tabelKey="dagafsluiting-facturen"
                  columns={[
                    {
                      key: "nummer", header: t("financieel.kolom.nummer"),
                      cell: (factuur) => (
                        /* In een NIEUW tabblad: je bent midden in een telling, en die wil je niet
                           kwijtraken omdat je even een betaling toevoegt. */
                        <a
                          className="fawkes-fin__link" href={`/facturen/${factuur.id}`}
                          target="_blank" rel="noreferrer"
                          title={t("dagafsluiting.factuurOpenen")}
                        >
                          {factuur.nummer || t("financieel.concept")}
                          <ExternalLink size={12} aria-hidden="true" />
                        </a>
                      ),
                    },
                    { key: "eigenaar", header: t("dagafsluiting.eigenaar"), cell: (f) => f.klantNaam || "—" },
                    { key: "dier", header: t("financieel.kolom.dier"), cell: (f) => f.dierNaam || "—" },
                    { key: "totaal", header: t("financieel.kolom.totaal"), align: "right",
                      cell: (f) => <span className="tabular">{formatEuro(f.totaal ?? 0, locale)}</span> },
                    { key: "betaald", header: t("financieel.kolom.betaald"), align: "right",
                      cell: (f) => <span className="tabular">{formatEuro(f.betaald ?? 0, locale)}</span> },
                    {
                      key: "openstaand", header: t("financieel.kolom.openstaand"), align: "right",
                      cell: (f) => (
                        <span className={(f.openstaand ?? 0) > 0 ? "tabular is-danger" : "tabular"}>
                          {formatEuro(f.openstaand ?? 0, locale)}
                        </span>
                      ),
                    },
                  ]}
                  rows={facturenVanDag?.data?.items ?? []}
                  getRowKey={(f) => String(f.id)}
                  empty=""
                />
              )}
              {/* De optelling stond in de voet van de tabel; die heeft een tabel met verschuifbare
                  kolommen niet, dus staat hij er als eigen regel onder. */}
              {!facturenBezig && (facturenVanDag?.data?.items ?? []).length > 0 && (
                <p className="fawkes-dag__lijsttotaal">
                  <span>{t("dagafsluiting.totaalFacturen")}</span>
                  <strong className="tabular">{formatEuro(som(facturenVanDag?.data?.items, "totaal"), locale)}</strong>
                  <strong className="tabular">{formatEuro(som(facturenVanDag?.data?.items, "betaald"), locale)}</strong>
                  <strong className="tabular">{formatEuro(som(facturenVanDag?.data?.items, "openstaand"), locale)}</strong>
                </p>
              )}
              {!facturenBezig && (facturenVanDag?.data?.items ?? []).length > 0 && (
                <p className="fawkes-dag__hint">{t("dagafsluiting.factuurTip")}</p>
              )}
            </div>
          )}

          <div className="fawkes-dag__tellen">
            <div className="fawkes-dag__telzij">
              <div className="fawkes-dag__blok">
                <h4>
                  <Banknote size={16} aria-hidden="true" /> {t("dagafsluiting.contantGeteld")}
                  <strong className="fawkes-dag__blok-bedrag">{formatEuro(getaldTotaal, locale)}</strong>
                </h4>
                <table className="fawkes-dag__teltabel">
                  <thead>
                    <tr>
                      <th>{t("dagafsluiting.biljetten")}</th>
                      <th className="is-num">{t("dagafsluiting.aantal")}</th>
                      <th className="is-num">{t("kasboek.kolom.bedrag")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BILJETTEN.map((eenheid) => {
                      const aantal = Number((aantallen[String(eenheid)] ?? "").trim() || 0);
                      return (
                        <tr key={eenheid}>
                          <td>{formatEuro(eenheid, locale)}</td>
                          <td className="is-num tabular">{Number.isFinite(aantal) ? aantal : 0}</td>
                          <td className="is-num tabular">
                            {formatEuro(Number.isFinite(aantal) ? aantal * eenheid : 0, locale)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td>{t("dagafsluiting.munten")}</td>
                      <td className="is-num tabular">
                        {MUNTEN.reduce((som, eenheid) =>
                          som + (Number((aantallen[String(eenheid)] ?? "").trim() || 0) || 0), 0)}
                      </td>
                      <td className="is-num tabular">{formatEuro(muntenTotaal, locale)}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan={2}>{t("dagafsluiting.totaalContant")}</th>
                      <td className="is-num tabular"><strong>{formatEuro(getaldTotaal, locale)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="fawkes-dag__blok">
                <h4>
                  <CreditCard size={16} aria-hidden="true" /> {t("dagafsluiting.pinGeteld")}
                  <strong className="fawkes-dag__blok-bedrag">{formatEuro(getaldPin, locale)}</strong>
                </h4>
                <dl className="fawkes-dag__lijst">
                  <Bedragregel label={t("dagafsluiting.pinTransacties")}
                    waarde={String(getaldPinAantal)} />
                  <Bedragregel label={t("dagafsluiting.pinBedrag")} waarde={formatEuro(getaldPin, locale)} />
                </dl>
              </div>
            </div>

            <div className="fawkes-dag__telzij">
              <div className="fawkes-dag__blok">
                <h4>{t("dagafsluiting.boekingenVandaag")}</h4>
                <p className="fawkes-dag__kop">{t("dagafsluiting.ontvangsten")}</p>
                <dl className="fawkes-dag__lijst">
                  <Bedragregel label={t("dagafsluiting.contant")} groen
                    waarde={formatEuro(boekingen.contantOntvangen, locale)} />
                  <Bedragregel label={t("dagafsluiting.pin")} groen
                    waarde={formatEuro(boekingen.pinOntvangen, locale)} />
                  <Bedragregel label={t("dagafsluiting.totaalOntvangsten")} sterk
                    waarde={formatEuro(boekingen.totaalOntvangen, locale)} />
                </dl>
                <p className="fawkes-dag__kop is-rood">{t("dagafsluiting.uitgavenKop")}</p>
                <dl className="fawkes-dag__lijst">
                  <Bedragregel label={t("dagafsluiting.uitgaven")} rood
                    waarde={formatEuro(boekingen.uitgaven, locale)} />
                </dl>
                <p className="fawkes-dag__kop">{t("dagafsluiting.beginSaldoKop")}</p>
                <dl className="fawkes-dag__lijst">
                  <Bedragregel label={t("dagafsluiting.beginSaldo")}
                    waarde={formatEuro(boekingen.beginSaldo, locale)} />
                  <Bedragregel label={t("dagafsluiting.verwachtEindsaldo")} sterk
                    waarde={formatEuro(boekingen.verwachtEindsaldo, locale)} />
                </dl>
              </div>

              {/* Contant en pin worden APART vergeleken: een pinbetaling komt nooit in de lade, dus een
                  tekort in het contant mag niet wegvallen tegen een overschot op de pin. */}
              {/* Alleen de bevestiging dat iets klopt; een afwijking staat al in de balk bovenaan. */}
              <Verschilblok
                titel={t("dagafsluiting.verschil.contant")}
                geteld={getaldTotaal}
                verwacht={boekingen.verwachtEindsaldo}
                geteldIsIngevuld={erIsGeteld}
                toonAfwijking={false}
                locale={locale}
                t={t}
              />
              <Verschilblok
                titel={t("dagafsluiting.verschil.pin")}
                geteld={getaldPin}
                verwacht={boekingen.pinOntvangen}
                geteldIsIngevuld={erIsGeteld}
                toonAfwijking={false}
                locale={locale}
                t={t}
              />
            </div>
          </div>

          <p className="fawkes-dag__tip">
            <Info size={14} aria-hidden="true" /> {t("dagafsluiting.stap3.tip")}
          </p>

          <footer className="fawkes-dag__acties">
            <Button variant="secondary" onClick={() => setStap(1)}>
              <ArrowLeft size={14} /> {t("dagafsluiting.vorige")}
            </Button>
            <Button onClick={() => setStap(3)}>
              {t("dagafsluiting.verder.vierogen")} <ArrowRight size={14} />
            </Button>
          </footer>
        </section>
      )}

      {!isLoading && !isError && afgesloten && (
        <section className="fawkes-dag__card">
          <div className={afgesloten.akkoord ? "fawkes-dag__verschil is-ok" : "fawkes-dag__verschil"}>
            <span aria-hidden="true">
              {afgesloten.akkoord ? <CheckCircle2 size={18} /> : <Clock size={18} />}
            </span>
            <div>
              <strong>
                {t(afgesloten.akkoord
                  ? "dagafsluiting.vastgelegd"
                  : "dagafsluiting.vastgelegdWacht")}
              </strong>
              <p>
                {t("dagafsluiting.vastgelegdUitleg", {
                  args: [
                    formatDatum(afgesloten.dag, locale),
                    afgesloten.afgeslotenDoor ?? "—",
                    afgesloten.afgeslotenOp ? formatDateTime(afgesloten.afgeslotenOp, locale) : "—",
                  ],
                })}
              </p>
              {afgesloten.akkoord && afgesloten.akkoordNaam && (
                <p>{t("dagafsluiting.akkoordDoorNaam", { args: [afgesloten.akkoordNaam] })}</p>
              )}
            </div>
          </div>

          <dl className="fawkes-dag__lijst">
            <Bedragregel label={t("dagafsluiting.getaldSaldo")}
              waarde={formatEuro(afgesloten.contantGeteld, locale)} />
            <Bedragregel label={t("dagafsluiting.verwachtEindsaldo")}
              waarde={formatEuro(afgesloten.contantVerwacht, locale)} />
            <Bedragregel label={t("dagafsluiting.verschilKop")} sterk
              rood={afgesloten.verschilContant !== 0}
              groen={afgesloten.verschilContant === 0}
              waarde={formatEuro(afgesloten.verschilContant, locale)} />
          </dl>
          {afgesloten.opmerking && (
            <div className="fawkes-dag__blok">
              <h4>{t("dagafsluiting.opmerkingKop")}</h4>
              <p>{afgesloten.opmerking}</p>
            </div>
          )}

          <footer className="fawkes-dag__acties">
            <Button variant="secondary" onClick={() => setAfgesloten(null)}>
              <ArrowLeft size={14} /> {t("dagafsluiting.terugNaarAfsluiting")}
            </Button>
            <Button onClick={() => { setAfgesloten(null); setStap(0); }}>
              {t("dagafsluiting.volgendeDag")} <ArrowRight size={14} />
            </Button>
          </footer>
        </section>
      )}

      {!isLoading && !isError && stap1 && stap === 3 && !afgesloten && (
        <section className="fawkes-dag__card">
          <header>
            <h3>{t("dagafsluiting.stap4.titel")}</h3>
            <p>{t("dagafsluiting.stap4.uitleg")}</p>
          </header>

          {/* Deze dag draagt al een afsluiting: dat moet je zien vóór je opnieuw telt, want opnieuw
              afsluiten corrigeert die afsluiting in plaats van er een tweede naast te zetten. */}
          {bestaande && bestaande.status !== "CONCEPT" && (
            <div className="fawkes-dag__verschil">
              <span aria-hidden="true"><Info size={18} /></span>
              <div>
                <strong>{t("dagafsluiting.alAfgesloten")}</strong>
                <p>
                  {t("dagafsluiting.vastgelegdUitleg", {
                    args: [
                      formatDatum(bestaande.dag, locale),
                      bestaande.afgeslotenDoor ?? "—",
                      bestaande.afgeslotenOp ? formatDateTime(bestaande.afgeslotenOp, locale) : "—",
                    ],
                  })}
                </p>
                <p>{t("dagafsluiting.alAfgeslotenUitleg")}</p>
              </div>
            </div>
          )}

          <div className="fawkes-dag__tellen">
            <div className="fawkes-dag__telzij">
              <div className="fawkes-dag__blok">
                <h4>{t("dagafsluiting.samenvatting")}</h4>
                <p className="fawkes-dag__kop">{t("dagafsluiting.ontvangsten")}</p>
                <dl className="fawkes-dag__lijst">
                  <Bedragregel label={t("dagafsluiting.contant")} groen
                    waarde={formatEuro(boekingen.contantOntvangen, locale)} />
                  <Bedragregel label={t("dagafsluiting.pin")} groen
                    waarde={formatEuro(boekingen.pinOntvangen, locale)} />
                  <Bedragregel label={t("dagafsluiting.totaalOntvangsten")} sterk
                    waarde={formatEuro(boekingen.totaalOntvangen, locale)} />
                </dl>
                <p className="fawkes-dag__kop is-rood">{t("dagafsluiting.uitgavenKop")}</p>
                <dl className="fawkes-dag__lijst">
                  <Bedragregel label={t("dagafsluiting.uitgaven")} rood
                    waarde={formatEuro(boekingen.uitgaven, locale)} />
                </dl>
                <p className="fawkes-dag__kop">{t("dagafsluiting.beginSaldoKop")}</p>
                <dl className="fawkes-dag__lijst">
                  <Bedragregel label={t("dagafsluiting.beginSaldo")}
                    waarde={formatEuro(boekingen.beginSaldo, locale)} />
                  <Bedragregel label={t("dagafsluiting.verwachtEindsaldo")} sterk
                    waarde={formatEuro(boekingen.verwachtEindsaldo, locale)} />
                  <Bedragregel label={t("dagafsluiting.getaldSaldo")} sterk
                    waarde={formatEuro(getaldTotaal, locale)} />
                </dl>
              </div>

              {/* De uitleg bij deze dag. Bij een verschil is dit het enige wat later nog vertelt waaróm
                  het verschil er was; daarom staat het veld hier en niet achter een knopje. */}
              <div className="fawkes-dag__blok">
                <h4>{t("dagafsluiting.opmerkingKop")}</h4>
                <label className="fui-field">
                  <textarea
                    className="fui-field__input fawkes-dag__opmerking" rows={4}
                    aria-label={t("dagafsluiting.opmerkingKop")}
                    placeholder={t("dagafsluiting.opmerkingHint")}
                    value={opmerking}
                    onChange={(e) => { setOpmerking(e.target.value); setAfsluitFout(null); }}
                  />
                </label>
              </div>
            </div>

            <div className="fawkes-dag__telzij">
              <div className="fawkes-dag__blok">
                <h4>{t("dagafsluiting.opsteller")}</h4>
                <div className="fawkes-dag__persoon">
                  <span className="fawkes-dag__avatar" aria-hidden="true">
                    {initialen(stap1.basis.afTeSluitenDoor)}
                  </span>
                  <div>
                    <strong>{stap1.basis.afTeSluitenDoor ?? "—"}</strong>
                    <p>{stap1.basis.laatsteBoeking
                      ? formatDateTime(stap1.basis.laatsteBoeking, locale)
                      : stap1.basis.datum}</p>
                  </div>
                  <StatusBadge tone="success">{t("dagafsluiting.voltooid")}</StatusBadge>
                </div>
              </div>

              {/* Het verschil staat naast de handtekeningen: wie tekent, ziet waarvoor hij tekent. */}
              <Verschilblok
                titel={t("dagafsluiting.verschil.contant")}
                geteld={getaldTotaal}
                verwacht={boekingen.verwachtEindsaldo}
                geteldIsIngevuld={erIsGeteld}
                locale={locale}
                t={t}
              />

              <div className="fawkes-dag__blok">
                <h4>{t("dagafsluiting.akkoordKop")}</h4>
                {laterControleren && !akkoord ? (
                  <div className="fawkes-dag__verschil">
                    <span aria-hidden="true"><Clock size={18} /></span>
                    <div>
                      <strong>{t("dagafsluiting.laterGekozen")}</strong>
                      <p>{t("dagafsluiting.laterGekozenUitleg")}</p>
                    </div>
                    <button type="button" className="fawkes-fin__link"
                      onClick={() => setLaterControleren(false)}>
                      {t("dagafsluiting.tochNu")}
                    </button>
                  </div>
                ) : collegas.length === 0 && !akkoord ? (
                  /* Eén account op deze omgeving: dan is er niemand om mee te controleren, en dat zeggen we
                     in plaats van een leeg keuzelijstje te tonen. */
                  <>
                    <p className="fawkes-dag__hint">{t("dagafsluiting.geenCollega")}</p>
                    <button
                      type="button" className="fawkes-fin__link fawkes-dag__later"
                      onClick={() => setLaterControleren(true)}
                    >
                      {t("dagafsluiting.laterControleren")}
                    </button>
                  </>
                ) : akkoord ? (
                  <div className="fawkes-dag__verschil is-ok">
                    <span aria-hidden="true"><CheckCircle2 size={18} /></span>
                    <div>
                      <strong>{t("dagafsluiting.akkoordGegeven")}</strong>
                      <p>{akkoord.naam}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="fui-field">
                      <span className="fui-field__label">{t("dagafsluiting.naamCollega")}</span>
                      <select
                        className="fui-field__input fui-select" value={collega}
                        onChange={(e) => { setCollega(e.target.value); setAkkoordFout(null); }}
                      >
                        <option value="">{t("dagafsluiting.selecteerCollega")}</option>
                        {collegas.map((c) => (
                          <option key={c.loginName} value={c.loginName}>{c.naam}</option>
                        ))}
                      </select>
                    </label>
                    <label className="fui-field">
                      <span className="fui-field__label">{t("dagafsluiting.wachtwoord")}</span>
                      <span className="fawkes-dag__wachtwoord">
                        <input
                          className="fui-field__input"
                          type={wachtwoordZichtbaar ? "text" : "password"}
                          autoComplete="off" value={wachtwoord}
                          placeholder={t("dagafsluiting.wachtwoordHint")}
                          onChange={(e) => { setWachtwoord(e.target.value); setAkkoordFout(null); }}
                        />
                        <button
                          type="button" title={t("dagafsluiting.wachtwoordTonen")}
                          aria-label={t("dagafsluiting.wachtwoordTonen")}
                          onClick={() => setWachtwoordZichtbaar((v) => !v)}
                        >
                          {wachtwoordZichtbaar ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </span>
                    </label>
                    <Button
                      variant="secondary"
                      disabled={!collega || !wachtwoord || akkoordBezig}
                      onClick={() => {
                        setAkkoordFout(null);
                        vraagAkkoord(
                          {
                            url: "dagafsluiting/akkoord",
                            method: "post",
                            values: { loginName: collega, wachtwoord },
                          },
                          {
                            onSuccess: (res) => {
                              const data = (res?.data ?? {}) as { loginName?: string; naam?: string };
                              setAkkoord({
                                loginName: data.loginName ?? collega,
                                naam: data.naam ?? collega,
                              });
                              // Het wachtwoord is gebruikt en verder nergens voor nodig.
                              setWachtwoord("");
                            },
                            onError: () => setAkkoordFout(t("dagafsluiting.akkoordFout")),
                          },
                        );
                      }}
                    >
                      <ShieldCheck size={14} /> {t("dagafsluiting.akkoordGeven")}
                    </Button>
                    {akkoordFout && (
                      <p className="fawkes-inboeken__field-error" role="alert">{akkoordFout}</p>
                    )}
                    <button
                      type="button" className="fawkes-fin__link fawkes-dag__later"
                      onClick={() => { setLaterControleren(true); setAkkoordFout(null); }}
                    >
                      {t("dagafsluiting.laterControleren")}
                    </button>
                  </>
                )}
              </div>

              <div className="fawkes-dag__info">
                <span aria-hidden="true"><Info size={16} /></span>
                <div>
                  <strong>{t("dagafsluiting.vierOgen")}</strong>
                  <p>{t("dagafsluiting.vierOgenUitleg")}</p>
                  <p>{t("dagafsluiting.vierOgenLater")}</p>
                </div>
              </div>
            </div>
          </div>

          <footer className="fawkes-dag__acties">
            <Button variant="secondary" onClick={() => setStap(2)}>
              <ArrowLeft size={14} /> {t("dagafsluiting.vorige")}
            </Button>
            <div className="fawkes-dag__afsluiten">
              {/* Afsluiten kan altijd. Zonder akkoord wordt de dag vastgelegd als "wacht op controle" —
                  een avonddienst alleen moet een getelde lade kunnen overdragen, anders belandt de telling
                  op een briefje in plaats van in het systeem. */}
              <Button
                disabled={afsluitBezig || vestigingId == null}
                onClick={() => {
                  setAfsluitFout(null);
                  legVast(
                    {
                      url: "dagafsluiting/afsluiten",
                      method: "post",
                      values: { ...afsluitPayload(), concept: false },
                    },
                    {
                      onSuccess: (res) => {
                        setAfgesloten((res?.data ?? null) as Afsluiting | null);
                        refetch();
                      },
                      onError: () => setAfsluitFout(t("dagafsluiting.afsluitFout")),
                    },
                  );
                }}
              >
                <Lock size={14} />{" "}
                {t(akkoord ? "dagafsluiting.dagAfsluiten" : "dagafsluiting.dagAfsluitenLater")}
              </Button>
              {/* De knop zegt wat er wordt vastgelegd: definitief, of met een controle die nog moet komen. */}
              {!akkoord && <small>{t("dagafsluiting.knopHintLater")}</small>}
              {afsluitFout && (
                <p className="fawkes-inboeken__field-error" role="alert">{afsluitFout}</p>
              )}
            </div>
          </footer>
        </section>
      )}

      {!isLoading && !isError && stap >= 4 && (
        <section className="fawkes-dag__card">
          <header>
            <h3>{t(`dagafsluiting.stap${stap + 1}.titel`)}</h3>
            <p>{t("dagafsluiting.nogTeBouwen")}</p>
          </header>
          <footer className="fawkes-dag__acties">
            <Button variant="secondary" onClick={() => setStap(stap - 1)}>
              <ArrowLeft size={14} /> {t("dagafsluiting.vorige")}
            </Button>
          </footer>
        </section>
      )}
    </div>
  );
}

/** One amount line in a step-3 block; the colour says what kind of amount it is. */
function Bedragregel({ label, waarde, groen, rood, sterk }: {
  label: string; waarde: string; groen?: boolean; rood?: boolean; sterk?: boolean;
}) {
  const klasse = ["fawkes-dag__regel", sterk ? "is-sterk" : "", groen ? "is-groen" : "", rood ? "is-rood" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={klasse}>
      <dt>{label}</dt>
      <dd className="tabular">{waarde}</dd>
    </div>
  );
}

/**
 * Counted against expected. Equal (to the cent) says so plainly; a difference names the amount and its
 * direction — "meer in de lade" or "minder in de lade" — because that is what the operator has to explain.
 */
function Verschilblok({ titel, geteld, verwacht, geteldIsIngevuld = true, toonAfwijking = true, locale, t }: {
  titel: string;
  geteld: number;
  verwacht: number;
  /** False zolang er nog niets geteld is: dan is er niets om een verschil van te maken. */
  geteldIsIngevuld?: boolean;
  /**
   * False waar een balk erboven het verschil al meldt (stap 3): hetzelfde bedrag twee keer op één scherm
   * maakt het niet duidelijker. Het groene "klopt"-blok blijft wel staan — dat zegt iets anders.
   */
  toonAfwijking?: boolean;
  locale: string;
  t: (k: string, options?: { args?: unknown[] }) => string;
}) {
  if (!geteldIsIngevuld) {
    return (
      <div className="fawkes-dag__verschil">
        <span aria-hidden="true"><Info size={18} /></span>
        <div>
          <strong>{titel}</strong>
          <p>{t("dagafsluiting.nogNietGeteld")}</p>
        </div>
      </div>
    );
  }
  const verschil = Math.round((geteld - verwacht) * 100) / 100;
  const klopt = verschil === 0;
  if (!klopt && !toonAfwijking) {
    return null;
  }
  return (
    <div className={`fawkes-dag__verschil${klopt ? " is-ok" : " is-afwijking"}`}>
      <span aria-hidden="true">{klopt ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}</span>
      <div>
        {/* Ook een groen blok noemt zijn onderwerp: "Alles klopt!" naast een rood verschilblok las als een
            scherm dat zichzelf tegenspreekt. */}
        <strong>{klopt ? t("dagafsluiting.kloptSoort", { args: [titel] }) : titel}</strong>
        <p>
          {klopt
            ? t("dagafsluiting.kloptUitleg")
            : t(verschil > 0 ? "dagafsluiting.meerInLade" : "dagafsluiting.minderInLade", {
              args: [formatEuro(Math.abs(verschil), locale)],
            })}
        </p>
      </div>
    </div>
  );
}

/**
 * One counting column: a denomination, the count you type, and what that comes to. Notes and coins each
 * get their own column so the whole drawer fits on one screen.
 */
function Telstaat({ titel, eenheden, soort, aantallen, setAantallen, locale, t }: {
  titel: string;
  eenheden: readonly number[];
  soort: "biljet" | "munt";
  aantallen: Record<string, string>;
  setAantallen: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  locale: string;
  t: (k: string, options?: { args?: unknown[] }) => string;
}) {
  const subtotaal = Math.round(eenheden.reduce((som, eenheid) => {
    const aantal = Number((aantallen[String(eenheid)] ?? "").trim() || 0);
    return som + (Number.isFinite(aantal) ? aantal * eenheid : 0);
  }, 0) * 100) / 100;

  return (
    <table className="fawkes-dag__teltabel">
      <thead>
        <tr>
          <th colSpan={5}>{titel}</th>
        </tr>
      </thead>
      <tbody>
        {eenheden.map((eenheid) => {
          const aantal = Number((aantallen[String(eenheid)] ?? "").trim() || 0);
          const regel = Number.isFinite(aantal) ? aantal * eenheid : 0;
          return (
            <tr key={eenheid}>
              <td className="fawkes-dag__eenheid">
                <span aria-hidden="true">
                  {soort === "biljet" ? <Banknote size={14} /> : <Coins size={14} />}
                </span>
                {formatEuro(eenheid, locale)}
              </td>
              <td className="fawkes-dag__maal" aria-hidden="true">×</td>
              <td>
                <input
                  className="fui-field__input fawkes-dag__aantal"
                  inputMode="numeric" value={aantallen[String(eenheid)] ?? ""}
                  aria-label={t("dagafsluiting.aantalVan", { args: [formatEuro(eenheid, locale)] })}
                  onChange={(e) => setAantallen((cur) => ({ ...cur, [String(eenheid)]: e.target.value }))}
                />
              </td>
              <td className="fawkes-dag__isgelijk" aria-hidden="true">=</td>
              <td className="is-num tabular">{formatEuro(regel, locale)}</td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <th colSpan={4}>{t("dagafsluiting.subtotaal")}</th>
          <td className="is-num tabular">{formatEuro(subtotaal, locale)}</td>
        </tr>
      </tfoot>
    </table>
  );
}

/**
 * The month calendar behind the date: a day that already carries a closing is green — filled when it was
 * approved, outlined when it was not — so you can see at a glance what is done. Days in the future cannot
 * be closed and are not clickable.
 */
function Kalender({ maand, gekozen, afgesloten, locale, t, onMaand, onKies, onSluit }: {
  maand: string;
  gekozen: string;
  afgesloten: Map<string, boolean>;
  locale: string;
  t: (k: string, options?: { args?: unknown[] }) => string;
  onMaand: (maand: string) => void;
  onKies: (dag: string) => void;
  onSluit: () => void;
}) {
  useEffect(() => {
    const buiten = (e: MouseEvent) => {
      if ((e.target as HTMLElement | null)?.closest(".fawkes-dag__kalender, .fawkes-dag__datumknop")) return;
      onSluit();
    };
    const toets = (e: KeyboardEvent) => { if (e.key === "Escape") onSluit(); };
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [onSluit]);

  const [jaar, maandNr] = maand.split("-").map(Number);
  const eerste = new Date(jaar, maandNr - 1, 1);
  const dagenInMaand = new Date(jaar, maandNr, 0).getDate();
  // Maandag als eerste kolom, zoals een Nederlandse kalender.
  const start = (eerste.getDay() + 6) % 7;
  const vandaag = vandaagIso();

  const cellen: (string | null)[] = [
    ...Array.from({ length: start }, () => null),
    ...Array.from({ length: dagenInMaand }, (_, i) =>
      `${maand}-${String(i + 1).padStart(2, "0")}`),
  ];

  const verschuif = (delta: number) => {
    const d = new Date(jaar, maandNr - 1 + delta, 1);
    onMaand(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  return (
    <div className="fawkes-dag__kalender" role="dialog" aria-label={t("dagafsluiting.kiesDag")}>
      <header>
        <button type="button" onClick={() => verschuif(-1)} aria-label={t("dagafsluiting.vorigeMaand")}>
          <ArrowLeft size={14} />
        </button>
        <strong>
          {new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(eerste)}
        </strong>
        <button type="button" onClick={() => verschuif(1)} aria-label={t("dagafsluiting.volgendeMaand")}>
          <ArrowRight size={14} />
        </button>
      </header>
      <div className="fawkes-dag__kalender-week" aria-hidden="true">
        {["ma", "di", "wo", "do", "vr", "za", "zo"].map((d) => <span key={d}>{d}</span>)}
      </div>
      <div className="fawkes-dag__kalender-grid">
        {cellen.map((datum, i) => {
          if (!datum) return <span key={`leeg-${i}`} />;
          const dagNr = Number(datum.slice(8));
          const toekomst = datum > vandaag;
          const isAf = afgesloten.has(datum);
          const akkoord = afgesloten.get(datum) === true;
          const klasse = [
            "fawkes-dag__kalender-dag",
            datum === gekozen ? "is-gekozen" : "",
            isAf ? (akkoord ? "is-afgesloten" : "is-open-afgesloten") : "",
            datum === vandaag ? "is-vandaag" : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              key={datum} type="button" className={klasse} disabled={toekomst}
              title={isAf
                ? t(akkoord ? "dagafsluiting.dagAfgesloten" : "dagafsluiting.dagAfgeslotenZonderAkkoord")
                : undefined}
              onClick={() => onKies(datum)}
            >
              {dagNr}
            </button>
          );
        })}
      </div>
      <p className="fawkes-dag__kalender-legenda">
        <span className="fawkes-dag__stip is-afgesloten" aria-hidden="true" /> {t("dagafsluiting.legendaAf")}
      </p>
    </div>
  );
}

/** De kolomtotalen onder de facturen van de dag: optellen wat er staat, niet een tweede bron. */
function som(
  rijen: { totaal: number | null; betaald: number | null; openstaand: number | null }[] | undefined,
  veld: "totaal" | "betaald" | "openstaand",
): number {
  return (rijen ?? []).reduce((totaal, rij) => totaal + (rij[veld] ?? 0), 0);
}

/** "248 kB" — een bestandsgrootte die een mens leest. */
function kilobytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

/**
 * "Opslaan als concept" — dezelfde knop op stap 1 en 2, met het tijdstip van de laatste keer ernaast.
 * Tellen is werk dat onderbroken wordt; dit parkeert de stand zonder de dag af te sluiten.
 */
function Conceptknop({ bezig, kan, op, onClick, locale, t }: {
  bezig: boolean;
  kan: boolean;
  op: string | null;
  onClick: () => void;
  locale: string;
  t: (k: string, options?: { args?: unknown[] }) => string;
}) {
  return (
    <span className="fawkes-dag__concept">
      <Button variant="secondary" disabled={bezig || !kan} onClick={onClick}>
        <Save size={14} /> {t("dagafsluiting.concept")}
      </Button>
      {op && <small>{t("dagafsluiting.conceptOp", { args: [formatDateTime(op, locale)] })}</small>}
    </span>
  );
}

/** De telstaat ("50=1;20=3") terug naar de invoervelden van stap 2. */
function uitTelstaat(telstaat: string | null): Record<string, string> {
  const uit: Record<string, string> = {};
  (telstaat ?? "").split(";").forEach((paar) => {
    const [eenheid, aantal] = paar.split("=");
    if (eenheid && aantal && Number(aantal) > 0) {
      uit[String(Number(eenheid))] = String(Number(aantal));
    }
  });
  return uit;
}

/** "€ 185" — hele euro's voor een tegel; de centen staan in het kasboek. */
function heleEuros(bedrag: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(bedrag ?? 0);
}

/** Today as a LOCAL yyyy-MM-dd — toISOString() would hand back yesterday before 02:00 CEST. */
function vandaagIso(): string {
  const nu = new Date();
  return `${nu.getFullYear()}-${String(nu.getMonth() + 1).padStart(2, "0")}-${String(nu.getDate()).padStart(2, "0")}`;
}

/** "M. Jansen" → "MJ": the avatar of whoever is closing, from the name we have. */
function initialen(naam: string | null | undefined): string {
  const delen = (naam ?? "").trim().split(/[\s.]+/).filter(Boolean);
  if (delen.length === 0) return "?";
  return (delen[0][0] + (delen.length > 1 ? delen[delen.length - 1][0] : "")).toUpperCase();
}

/** A euro amount from a free-text field (comma or dot); anything unparseable counts as nothing. */
function bedragVan(waarde: string): number {
  const n = Number((waarde ?? "").trim().replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function Kpi({ icon, value, label, accent }: {
  icon: React.ReactNode; value: string; label: string; accent?: boolean;
}) {
  return (
    <div className={`fawkes-fin__kpi${accent ? " is-accent" : ""}`}>
      <span className="fawkes-fin__kpi-icon" aria-hidden="true">{icon}</span>
      <span className="fawkes-fin__kpi-text">
        <small>{label}</small>
        <strong>{value}</strong>
      </span>
    </div>
  );
}

/** One fact of the closing with its own check: green when it is in order, plain when it is not. */
function Regel({ icon, label, waarde, ok }: {
  icon: React.ReactNode; label: string; waarde: string | null | undefined; ok: boolean;
}) {
  return (
    <div className="fawkes-dag__regel">
      <dt>
        <span className={`fawkes-dag__vink${ok ? " is-ok" : ""}`} aria-hidden="true">
          {ok ? <CheckCircle2 size={16} /> : icon}
        </span>
        {label}
      </dt>
      <dd>{waarde?.trim() || "—"}</dd>
    </div>
  );
}

function Cijfer({ icon, waarde, label }: { icon: React.ReactNode; waarde: string; label: string }) {
  return (
    <div className="fawkes-dag__cijfer">
      <span className="fawkes-dag__cijfer-icon" aria-hidden="true">{icon}</span>
      <strong>{waarde}</strong>
      <small>{label}</small>
    </div>
  );
}

function Punt({ ok, tekst }: { ok: boolean; tekst: string }) {
  return (
    <li className={ok ? "is-ok" : undefined}>
      <span aria-hidden="true">{ok ? <CheckCircle2 size={16} /> : <Info size={16} />}</span> {tekst}
    </li>
  );
}
